import {mountBrand} from './brand.js';
import {currentLanguage, t} from './i18n.js';
import {icon} from './icons.js';
import {escapeHtml as esc, previewUrl, readTour, applyPreviewFallback} from './tour-data.js';
import {mountHotspots} from './hotspots.js';
import {defaultRadarRange} from './tour-radar.js';

export function mountTourUI(player, skin) {
 const root = document.createElement('div');
 root.className = 'km-tour';
 root.dataset.panel = '';
 document.getElementById('container').appendChild(root);
 document.documentElement.lang = currentLanguage();
 let nodes = [], active = null, panel = '', filter = 'all', query = '', tourMap = null, mapPromise = null;
 let clean = false, rotating = false, film = !matchMedia('(max-width: 640px)').matches, initialized = false;
 let radarRange = null;
 let toastTimer, hintTimer, lastTrigger, loadingTimer;
 const visited = new Set();
 const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
 const button = (action, image, label, extra = '') => `<button type="button" class="km-icon-button" data-action="${action}" aria-label="${t(label)}" title="${t(label)}" ${extra}>${icon(image)}</button>`;
 const languageUrl = new URL(location.href);
 languageUrl.searchParams.set('lang', currentLanguage() === 'el' ? 'en' : 'el');
 root.innerHTML = `
  <div class="km-shade km-shade--top"></div><div class="km-shade km-shade--bottom"></div>
  <header class="km-header"><div class="km-header__brand"></div><span class="km-header__caption">360° <span>·</span> ${t('tour')}</span>
   <div class="km-header__tools">${button('help','help','help')}${button('clean','eye','hideUi')}${button('fullscreen','expand','fullscreen')}
    <a class="km-language" href="${esc(languageUrl.href)}" aria-label="${t('language')}">${currentLanguage() === 'el' ? 'EN' : 'EL'}</a></div>
  </header>
  <button type="button" class="km-compass" data-action="north" aria-label="${t('north')}" title="${t('north')}"><span>N</span><svg viewBox="0 0 40 40" aria-hidden="true"><path d="m20 6-6 14 6-3 6 3Z" fill="currentColor"/><path d="m20 34 6-14-6 3-6-3Z" fill="currentColor" opacity=".28"/></svg></button>
  <div class="km-load" role="progressbar" aria-label="${t('loading')}" aria-valuemin="0" aria-valuemax="100" aria-valuenow="0" hidden><span></span></div>
  <div class="km-hint">${icon('eye')}<span>${t('hint')}</span></div>
  <section class="km-panel" id="km-panel" aria-labelledby="km-panel-title" tabindex="-1" hidden>
   <header class="km-panel__header"><div><span class="km-eyebrow">${t('explore')} · ${t('name')}</span><h2 id="km-panel-title"></h2></div>${button('close','close','close')}</header>
   <div class="km-panel__content"></div>
  </section>
  <footer class="km-bottom"><div class="km-bottom__bar">
   <div class="km-location"><div class="km-location__number"><span>01</span><small>/ —</small></div><div class="km-location__text"><span class="km-eyebrow">${t('location')}</span><h1>${t('loading')}</h1><p></p></div></div>
   <nav class="km-dock" aria-label="${t('explore')}">
    <button type="button" class="km-dock__main" data-action="places" aria-label="${t('places')}" aria-controls="km-panel" aria-expanded="false">${icon('grid')}<span>${t('places')}</span><small class="km-count"></small></button>
    <button type="button" data-action="map" aria-label="${t('map')}" aria-controls="km-panel" aria-expanded="false">${icon('map')}<span>${t('map')}</span></button>
    <button type="button" data-action="info" aria-label="${t('info')}" aria-controls="km-panel" aria-expanded="false">${icon('info')}<span>${t('info')}</span></button>
    <span class="km-dock__divider"></span>${button('rotate','rotate','rotate','aria-pressed="false"')}${button('film','film','thumbnails',`aria-pressed="${film}"`)}
   </nav></div>
   <div class="km-film-wrap"><div class="km-film" aria-label="${t('places')}"></div></div>
   <div class="km-bottom__meta"><span class="km-visited"></span><span>360°</span></div>
  </footer>
  <div class="km-view-tools">${button('zoomIn','plus','zoomIn')}${button('zoomOut','minus','zoomOut')}${button('share','share','share')}</div>
  <button type="button" class="km-restore km-icon-button" data-action="clean" aria-label="${t('showUi')}" title="${t('showUi')}" hidden>${icon('eye')}</button>
  <div class="km-toast" role="status" hidden></div>`;
 mountBrand(root.querySelector('.km-header__brand'));
 root.classList.toggle('has-film', film);
 const $ = (selector) => root.querySelector(selector);
 root.querySelectorAll('.km-dock [aria-expanded]').forEach((el) => el.disabled = true);
 $('.km-film-wrap').inert = !film;
 // Reserve exactly the footer's height, including long titles and the filmstrip.
 const footerSize = new ResizeObserver(() => {
  root.style.setProperty('--km-panel-bottom', `${root.getBoundingClientRect().bottom - $('.km-bottom').getBoundingClientRect().top + 16}px`);
 });
 footerSize.observe($('.km-bottom'));
 const announce = (message) => {
  clearTimeout(toastTimer); $('.km-toast').textContent = message; $('.km-toast').hidden = false;
  toastTimer = setTimeout(() => $('.km-toast').hidden = true, 4200);
 };
 function dismissHint() { $('.km-hint').classList.add('is-dismissed'); }
 function openNode(id, closePanel = true) {
  if (!nodes.some((node) => node.id === id)) return;
  if (closePanel) setPanel('');
  dismissHint();
  if (id !== player.getCurrentNode()) player.openNext(`{${id}}`, '');
 }
 function currentNode() { return nodes.find((node) => node.id === player.getCurrentNode()) || nodes[0]; }
 function nodeCard(node, compact = false) {
  return `<button type="button" class="${compact ? 'km-thumb' : 'km-place'}" data-node="${esc(node.id)}" aria-label="${esc(node.title)}" aria-current="${node.id === active?.id ? 'true' : 'false'}">
   <img src="${previewUrl(node.id)}" alt="" loading="lazy"><span class="km-place__index">${String(node.index + 1).padStart(2,'0')}</span>
   <span class="km-place__visited" ${visited.has(node.id) ? '' : 'hidden'}>${icon('check')}<span class="km-sr-only">${t('visited')}</span></span>
   <span class="km-place__caption"><strong>${esc(node.title)}</strong>${compact ? '' : `<small>${t(node.aerial ? 'aerialPanorama' : 'panorama')}${node.date ? ` · ${node.date.slice(0,4)}` : ''}</small>`}</span>
  </button>`;
 }
 function renderFilm() { $('.km-film').innerHTML = nodes.map((node) => nodeCard(node, true)).join(''); }
 function renderPlaces() {
  $('.km-panel__content').innerHTML = `<div class="km-search">${icon('search')}<input type="search" aria-label="${t('search')}" placeholder="${t('search')}" value="${esc(query)}"></div>
   <div class="km-filters" role="group" aria-label="${t('places')}">${['all','ground','aerial'].map((key) => `<button type="button" data-filter="${key}" aria-pressed="${filter === key}">${t(key)}</button>`).join('')}</div>
   <div class="km-places-grid"></div><p class="km-language-note">${t('languageNote')}</p>`;
  renderPlacesGrid();
 }
 function renderPlacesGrid() {
  const normalize = (text) => text.normalize('NFD').replace(/[\u0300-\u036f]/g,'').toLocaleLowerCase(currentLanguage());
  const matching = nodes.filter((node) => (filter === 'all' || node.aerial === (filter === 'aerial')) && normalize(node.title).includes(normalize(query)));
  $('.km-places-grid').innerHTML = matching.length ? matching.map((node) => nodeCard(node)).join('') : `<p class="km-empty">${t('empty')}</p>`;
 }
 function renderInfo() {
  if (!active) return;
  const date = active.date ? new Intl.DateTimeFormat(currentLanguage(), {dateStyle:'long'}).format(new Date(`${active.date}T12:00:00`)) : '';
  $('.km-panel__content').innerHTML = `<div class="km-info-image"><img src="${previewUrl(active.id)}" alt=""></div>
   <span class="km-info-kind">${icon(active.aerial ? 'aerial' : 'horizon')}${t(active.aerial ? 'aerialPanorama' : 'panorama')}</span>
   ${active.description ? `<p class="km-description">${esc(active.description)}</p>` : ''}
   <dl class="km-details">${date ? `<div><dt>${t('capture')}</dt><dd>${date}</dd></div>` : ''}
    ${active.coords ? `<div><dt>${t('coordinates')}</dt><dd>${active.coords[1].toFixed(5)}° N · ${active.coords[0].toFixed(5)}° E</dd></div>` : ''}
    ${active.author ? `<div><dt>${t('photoBy')}</dt><dd>${esc(active.author)}</dd></div>` : ''}</dl>
   <button type="button" class="km-text-button" data-action="share">${icon('share')}${t('share')}</button>
   <h3>${t('connected')}</h3><div class="km-connections">${active.links.length ? active.links.map((id) => {
    const node = nodes.find((node) => node.id === id);
    return `<button type="button" data-node="${esc(id)}"><img src="${previewUrl(id)}" alt=""><span>${esc(node.title)}</span>${icon('right')}</button>`;
   }).join('') : `<p class="km-empty">${t('noConnections')}</p>`}</div>
   <p class="km-developer-credit">Developed by <a target="_blank" rel="noopener noreferrer" href="https://www.geotour.gr">Geotour Crete</a></p>`;
 }
 async function renderMap() {
  $('.km-panel__content').innerHTML = `<div class="km-tour-map"><div class="km-map-status" role="status">${t('loadingMap')}</div><div class="km-tour-map__canvas km-map--fill" aria-label="${t('map')}"></div></div>
   <div class="km-map-footer"><span><i></i>${t('currentView')}</span><button type="button" class="km-text-button" data-action="fit">${icon('target')}${t('fitMap')}</button></div>
   <div class="km-radar-controls"><label>${t('radarRange')}<select data-radar-range>${['auto',1000,3000,10000,20000,50000].map((value) => `<option value="${value}" ${radarRange === (value === 'auto' ? null : value) ? 'selected' : ''}>${value === 'auto' ? t('autoRange') : `${value/1000} km`}</option>`).join('')}</select></label><button type="button" class="km-text-button" data-action="fitView">${icon('compass')}${t('showView')}</button></div>
   <p class="km-map-hint">${t('mapHint')}</p>`;
  syncRadarRange();
  const canvas = $('.km-tour-map__canvas');
  try {
   mapPromise ||= import('./tour-map.js');
   const {createTourMap} = await mapPromise;
   if (panel !== 'map' || !canvas.isConnected) return;
   tourMap = createTourMap(canvas, nodes, (id) => openNode(id), player, currentLanguage(), () => { if (canvas.isConnected && panel === 'map') $('.km-map-status').hidden = true; }, () => {
    if (canvas.isConnected && panel === 'map') {
     $('.km-map-status').hidden = false;
     $('.km-map-status').innerHTML = `${t('mapError')}<button type="button" data-action="retry">${t('retry')}</button>`;
    }
   });
   tourMap.update(active);
   tourMap.setRange(radarRange);
  } catch (error) {
   console.warn('Kamilari map unavailable', error);
   if (panel === 'map' && canvas.isConnected) $('.km-map-status').innerHTML = `${t('mapError')}<button type="button" data-action="retry">${t('retry')}</button>`;
  }
 }
 function syncRadarRange() {
  const option = $('[data-radar-range] option[value="auto"]');
  if (option) option.textContent = `${t('autoRange')} · ${defaultRadarRange(active)/1000} km`;
 }
 function setPanel(next, trigger) {
  if (next && next === panel) next = '';
  if (next && film && matchMedia('(max-width:640px)').matches) {
   film = false; root.classList.remove('has-film'); $('.km-film-wrap').inert = true;
   $('[data-action="film"]').setAttribute('aria-pressed','false');
  }
  tourMap?.destroy(); tourMap = null;
  panel = next; root.dataset.panel = panel;
  $('.km-view-tools').inert = !!panel || clean;
  root.classList.toggle('panel-open', !!panel);
  $('.km-panel').hidden = !panel;
  root.querySelectorAll('.km-dock [aria-expanded]').forEach((el) => el.setAttribute('aria-expanded', String(el.dataset.action === panel)));
  if (panel) {
   lastTrigger = trigger || root.querySelector(`[data-action="${panel}"]`);
   $('.km-panel__content').replaceChildren();
   $('#km-panel-title').textContent = panel === 'info' ? active?.title || t('info') : t(panel);
   if (panel === 'places') renderPlaces();
   if (panel === 'info') renderInfo();
   if (panel === 'map') renderMap();
   if (panel === 'help') $('.km-panel__content').innerHTML = `<div class="km-help-illustration">${icon('eye')}<span>360°</span></div><p class="km-help-text">${t('helpText')}</p>`;
   $('.km-panel').focus({preventScroll: true});
   dismissHint();
  } else { lastTrigger?.focus({preventScroll: true}); }
 }
 function updateNode() {
  active = currentNode();
  if (!active) return;
  visited.add(active.id);
  const number = String(active.index + 1).padStart(2,'0');
  $('.km-location__number > span').textContent = number;
  $('.km-location__number > small').textContent = `/ ${String(nodes.length).padStart(2,'0')}`;
  $('.km-location h1').textContent = active.title;
  $('.km-location p').textContent = `${t(active.aerial ? 'aerialPanorama' : 'panorama')}${active.date ? ` · ${active.date.slice(0,4)}` : ''}`;
  $('.km-location').classList.remove('km-location--enter');
  requestAnimationFrame(() => $('.km-location').classList.add('km-location--enter'));
  $('.km-count').textContent = nodes.length;
  $('.km-visited').textContent = `${visited.size} / ${nodes.length} · ${t('visited')}`;
  root.querySelectorAll('[data-node]').forEach((el) => {
   el.setAttribute('aria-current', String(el.dataset.node === active.id));
   const mark = el.querySelector('.km-place__visited'); if (mark) mark.hidden = !visited.has(el.dataset.node);
  });
  const thumbnail = $(`.km-thumb[data-node="${active.id}"]`);
  if (thumbnail && film) $('.km-film').scrollTo({left: thumbnail.offsetLeft - $('.km-film').clientWidth / 2 + thumbnail.clientWidth / 2, behavior: reducedMotion.matches ? 'instant' : 'smooth'});
  if (panel === 'info') { $('#km-panel-title').textContent = active.title; renderInfo(); }
  tourMap?.update(active);
  syncRadarRange();
  syncHeading();
  // replaceState avoids invoking the template's hashchange handler and reopening the node.
  const url = new URL(location.href); url.hash = active.id; history.replaceState(null,'',url);
  languageUrl.hash = active.id; $('.km-language').href = languageUrl.href;
  document.title = `${active.title} · ${t('name')} 360°`;
 }
 async function initialize() {
  if (initialized || !player.getNodeIds().length) return;
  initialized = true;
  try { nodes = await readTour(player); }
  catch (error) {
   console.warn('Tour metadata unavailable', error);
   // Keep navigation available even if the XML metadata request fails.
   nodes = player.getNodeIds().map((id,index) => ({id,index,title:player.getNodeUserdata(id)?.title || id,links:[],coords:null,aerial:false}));
   announce(t('loadError'));
  }
  renderFilm(); updateNode(); refreshHotspots();
  root.querySelectorAll('.km-dock [aria-expanded]').forEach((el) => el.disabled = false);
  root.classList.add('is-ready');
  hintTimer = setTimeout(dismissHint, 8500);
 }
 function loading(show) {
  clearTimeout(loadingTimer);
  if (show) { $('.km-load').hidden = false; $('.km-load').style.setProperty('--progress','8%'); loadingTimer = setTimeout(() => loading(false), 12000); }
  else { $('.km-load').hidden = true; root.classList.remove('is-transitioning'); }
 }
 root.addEventListener('click', async (event) => {
  const node = event.target.closest('[data-node]');
  if (node) { openNode(node.dataset.node); return; }
  const selectedFilter = event.target.closest('[data-filter]');
  if (selectedFilter) {
   filter = selectedFilter.dataset.filter;
   root.querySelectorAll('[data-filter]').forEach((el) => el.setAttribute('aria-pressed', String(el === selectedFilter)));
   renderPlacesGrid(); return;
  }
  const trigger = event.target.closest('[data-action]'); if (!trigger) return;
  const action = trigger.dataset.action;
  if (['places','map','info','help'].includes(action)) { setPanel(action, trigger); return; }
  if (action === 'close') setPanel('');
  if (action === 'fullscreen') {
   try {
    if (document.fullscreenElement) await document.exitFullscreen();
    else if (player.getIsFullscreen()) player.exitFullscreen();
    else if (root.parentElement.requestFullscreen) await root.parentElement.requestFullscreen();
    else player.toggleFullscreen();
   } catch { announce(t('fullscreenUnavailable')); }
  }
  if (action === 'rotate') {
   rotating = !rotating;
   if (rotating) player.startAutorotate(0.6, 1, 0); else player.stopAutorotate();
   syncRotation();
  }
  if (action === 'film') {
   if (panel && matchMedia('(max-width:640px)').matches) setPanel('');
   film = !film; root.classList.toggle('has-film', film); $('.km-film-wrap').inert = !film; trigger.setAttribute('aria-pressed', String(film));
  }
  if (action === 'clean') {
   clean = !clean; if (clean) setPanel(''); root.classList.toggle('is-clean', clean);
   $('.km-restore').hidden = !clean;
   for (const selector of ['.km-header','.km-bottom','.km-view-tools','.km-compass']) $(selector).inert = clean;
   (clean ? $('.km-restore') : $('[data-action="clean"]')).focus(); dismissHint();
  }
  if (action === 'zoomIn' || action === 'zoomOut') player.changeFov(action === 'zoomIn' ? -10 : 10);
  if (action === 'north') player.moveTo(player.getPan() - player.getPanNorth(), 0, player.getFov(), 2);
  if (action === 'fit') tourMap?.fit();
  if (action === 'fitView') tourMap?.fitView();
  if (action === 'retry') { tourMap?.destroy(); tourMap = null; mapPromise = null; renderMap(); }
  if (action === 'share') {
   const url = new URL(location.href); url.hash = `${active.id},${player.getPan().toFixed(1)},${player.getTilt().toFixed(1)},${player.getFov().toFixed(1)}`;
   try { await navigator.clipboard.writeText(url.href); announce(t('copied')); }
   catch { history.replaceState(null,'',url); announce(t('copyFailed')); }
  }
 });
 root.addEventListener('input', (event) => { if (event.target.type === 'search') {query = event.target.value; renderPlacesGrid();} });
 root.addEventListener('change', (event) => {
  if (event.target.matches('[data-radar-range]')) {
   radarRange = event.target.value === 'auto' ? null : Number(event.target.value);
   tourMap?.setRange(radarRange);
  }
 });
 root.addEventListener('error', (event) => {
  if (event.target.tagName !== 'IMG') return;
  if (applyPreviewFallback(event.target,nodes)) event.stopImmediatePropagation();
  else {event.target.hidden = true; event.target.parentElement.classList.add('has-no-image');}
 }, true);
 document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;
  if (clean) $('[data-action="clean"]').click(); else if (panel) setPanel('');
 });
 function syncRotation() {
  rotating = player.getIsAutorotating?.() ?? rotating;
  const el = $('[data-action="rotate"]'); el.setAttribute('aria-pressed',String(rotating));
  el.title = el.ariaLabel = t(rotating ? 'stopRotate' : 'rotate');
 }
 function syncFullscreen() {
  const el = $('[data-action="fullscreen"]'), enabled = !!document.fullscreenElement || player.getIsFullscreen();
  el.innerHTML = icon(enabled ? 'collapse' : 'expand'); el.title = el.ariaLabel = t(enabled ? 'exitFullscreen' : 'fullscreen');
 }
 const refreshHotspots = mountHotspots(player, skin, (id) => nodes.find((node) => node.id === id));
 player.addListener('configloaded', initialize);
 player.addListener('changenode', updateNode);
 player.addListener('beforechangenode', () => { loading(true); root.classList.add('is-transitioning'); });
 player.addListener('imagesready', () => loading(false));
 player.addListener('downloadprogress', () => {
  const progress = Math.round(player.getPercentLoaded() * 100);
  $('.km-load').setAttribute('aria-valuenow',String(progress)); $('.km-load').style.setProperty('--progress',`${Math.max(8,progress)}%`);
 });
 function syncHeading() {
  // North stays fixed on the dial; the arrow shows the viewing bearing, as on the map.
  // Pano2VR pan increases counterclockwise; CSS rotation increases clockwise.
  $('.km-compass svg').style.transform = `rotate(${-player.getPanNorth()}deg)`;
  tourMap?.heading();
 }
 player.addListener('positionchanged', syncHeading);
 player.addListener('playerdown', dismissHint);
 player.addListener('autorotatechanged', syncRotation);
 player.addListener('fullscreenenter', syncFullscreen); player.addListener('fullscreenexit', syncFullscreen);
 document.addEventListener('fullscreenchange', syncFullscreen);
 document.addEventListener('visibilitychange', () => { if (document.hidden && rotating) {player.stopAutorotate(); rotating = false; syncRotation();} });
  window.addEventListener('pagehide', () => {tourMap?.destroy(); footerSize.disconnect(); clearTimeout(toastTimer); clearTimeout(hintTimer); clearTimeout(loadingTimer);});
 initialize();
}
